// The Dashboard's pipeline picture (owner, 2026-11-04 — D-461: "Let's try
// something with the Pipeline display at the top"). One card per step, left to
// right in the order invoices move, Denial Review set apart under the main path.
//
// Each card shows how many invoices wait there now and their dollars, and a bar
// that runs the card's full width, split by how long ago the step's invoices
// were uploaded — the same age ramp as the Dashboard's Days Since Uploaded Date
// bar, so the two read together. The step holding the most invoices in the
// oldest band gets a red outline. Denial Review sits at the end of the row,
// past a rule.
//
// EACH BAR IS ITS OWN STEP'S 100% (owner, 2026-11-04 — D-469: "use color coding
// to show the age groups as a percentage of the big number in the same step
// box. There should be no empty space at the end of a bar"). The segments are
// that step's own count split by age, so they always fill the track; a bar is
// never scaled to the busiest step. A step with nothing in it draws the bare
// neutral track. The "Usually N days here" / "No finished work yet" line went
// in the same change.
//
// Adapted from design pitch B (2026-11-03). Read-only, and fed entirely by the
// screen: it makes no reads of its own.
import { Caption1, Text, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import type { AgeBandTile } from '../api/types'
import { TIMELINE_NODES, type NodeKey, type TimelineNode } from '../lib/constants'
import { formatCurrency } from '../lib/format'
import { AGE_RAMP, ageRampFill } from '../lib/theme'
import { pipelineBarSegments, type StepFigures } from '../lib/dashboardFigures'

export type { StepFigures } from '../lib/dashboardFigures'

const EMPTY_STEP: StepFigures = { count: 0, total: 0, medianDays: null, ages: null }

const nodeByKey = new Map<NodeKey, TimelineNode>(TIMELINE_NODES.map((node) => [node.key, node]))
const node = (key: NodeKey): TimelineNode => nodeByKey.get(key)!

/** The main path, left to right. An inner array is a pair of alternatives
    drawn one above the other (Controller Approval over Manager Approval). */
const MAIN_PATH: readonly (NodeKey | readonly NodeKey[])[] = [
  'import-control',
  ['controller', 'department'],
  'director',
  'oversight',
  'export-control',
  'closing-confirmation',
]

const invoices = (n: number) => (n === 1 ? '1 invoice' : `${n} invoices`)

function bandLabel(band: AgeBandTile): string {
  if (band.max_days === null) return `${band.min_days}+ days`
  if (band.max_days === band.min_days) return band.min_days === 1 ? '1 day' : `${band.min_days} days`
  return `${band.min_days}-${band.max_days} days`
}

const useStyles = makeStyles({
  sub: { color: tokens.colorNeutralForeground3 },
  flowScroll: { overflowX: 'auto' },
  // Wide enough for the main path and Denial Review on one row.
  flow: { display: 'flex', alignItems: 'center', minWidth: '1180px' },
  pair: { display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 0', minWidth: 0 },
  or: { textAlign: 'center', color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  arrow: { flex: '0 0 28px', display: 'flex', justifyContent: 'center', color: tokens.colorNeutralStroke1 },
  card: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '12px 14px',
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  // A step an invoice visits only when a routing rule requires it.
  cardOptional: {
    borderTopStyle: 'dashed',
    borderRightStyle: 'dashed',
    borderBottomStyle: 'dashed',
    borderLeftStyle: 'dashed',
  },
  cardIdle: { backgroundColor: tokens.colorNeutralBackground2, boxShadow: 'none' },
  cardHot: { border: `2px solid ${AGE_RAMP[AGE_RAMP.length - 1]}`, padding: '11px 13px' },
  pastDue: { color: tokens.colorPaletteRedForeground1, fontWeight: tokens.fontWeightSemibold },
  // A step name wraps rather than truncating: "Controller A…" names nothing.
  cardTitle: { fontWeight: tokens.fontWeightSemibold, lineHeight: '18px' },
  // Wraps so a long dollar figure drops under the count rather than overflowing a narrow card.
  countRow: { display: 'flex', alignItems: 'baseline', columnGap: '6px', flexWrap: 'wrap' },
  count: { fontSize: '28px', lineHeight: '32px', fontWeight: tokens.fontWeightSemibold },
  countIdle: { color: tokens.colorNeutralForeground4 },
  track: {
    display: 'flex',
    height: '10px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    overflow: 'hidden',
  },
  segment: { height: '100%' },
  inlineRule: { alignSelf: 'stretch', width: '1px', margin: '0 14px', backgroundColor: tokens.colorNeutralStroke2, flexShrink: 0 },
})

function Arrow() {
  const s = useStyles()
  return (
    <div className={s.arrow} aria-hidden="true">
      <svg width="24" height="14" viewBox="0 0 24 14">
        <path d="M1 7 H20 M15 2 L21 7 L15 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
}

function StepCard({ step, figures, bands, hot }: {
  step: TimelineNode
  figures: StepFigures
  bands: readonly AgeBandTile[]
  hot: boolean
}) {
  const s = useStyles()
  const idle = figures.count === 0
  const segments = pipelineBarSegments(figures)
  const drawn = segments.reduce((sum, segment) => sum + segment.count, 0)
  const summary = `${step.label}: ${invoices(figures.count)}, ${formatCurrency(figures.total)}.`

  return (
    <div
      className={mergeClasses(
        s.card,
        hot && s.cardHot,
        step.optional === true && s.cardOptional,
        idle && s.cardIdle,
      )}
      role="group"
      aria-label={summary}
      title={summary}
    >
      <Text size={300} className={s.cardTitle}>{step.label}</Text>
      <div className={s.countRow}>
        <span className={mergeClasses(s.count, idle && s.countIdle)}>{figures.count}</span>
        <Caption1 className={s.sub}>{formatCurrency(figures.total)}</Caption1>
      </div>
      {/* The track IS the bar: segments grow by count, so together they fill
          it with no rounding gap at the end. An idle step draws the bare track. */}
      <div className={s.track}>
        {segments.map((segment) => (
          <div
            key={segment.band ?? 'none'}
            className={s.segment}
            style={{
              flexGrow: segment.count,
              backgroundColor:
                segment.band === null
                  ? figures.ages === null
                    ? tokens.colorNeutralForeground3
                    : tokens.colorNeutralStroke1
                  : ageRampFill(segment.band, bands.length),
            }}
            title={
              segment.band === null
                ? undefined
                : `${bands[segment.band] ? bandLabel(bands[segment.band]!) : ''} since uploaded: ${invoices(segment.count)} (${Math.round((segment.count / drawn) * 100)}%)`
            }
          />
        ))}
      </div>
      {figures.pastDue !== undefined && figures.pastDue > 0 && (
        <Caption1 className={s.pastDue}>{figures.pastDue} past due</Caption1>
      )}
    </div>
  )
}

interface Props {
  /** Per step node key. A step with no row draws as idle. */
  figures: ReadonlyMap<string, StepFigures>
  /** The uploaded-age bands the cards' bars are split by. */
  bands: readonly AgeBandTile[]
}

export function PipelineFlow({ figures, bands }: Props) {
  const s = useStyles()
  const figuresFor = (key: NodeKey) => figures.get(key) ?? EMPTY_STEP
  const workSteps = TIMELINE_NODES.filter((n) => n.archive !== true)

  // The step with the most invoices in the oldest band, if any has one.
  let oldest: { key: NodeKey; n: number } | null = null
  for (const n of workSteps) {
    const ages = figures.get(n.key)?.ages
    const tail = ages ? (ages[ages.length - 1] ?? 0) : 0
    if (tail > 0 && (oldest === null || tail > oldest.n)) oldest = { key: n.key, n: tail }
  }

  const card = (key: NodeKey) => (
    <StepCard
      key={key}
      step={node(key)}
      figures={figuresFor(key)}
      bands={bands}
      hot={oldest?.key === key}
    />
  )

  return (
    <div>
      <div className={s.flowScroll}>
        <div className={s.flow}>
          {MAIN_PATH.map((entry, i) => (
            <div key={typeof entry === 'string' ? entry : entry.join('|')} style={{ display: 'contents' }}>
              {i > 0 && <Arrow />}
              {typeof entry === 'string' ? (
                card(entry)
              ) : (
                <div className={s.pair}>
                  {entry.map((key, j) => (
                    <div key={key} style={{ display: 'contents' }}>
                      {j > 0 && <Caption1 className={s.or}>or</Caption1>}
                      {card(key)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className={s.inlineRule} aria-hidden />
          {card('denial-review')}
        </div>
      </div>
    </div>
  )
}
